import { Box, Text, TouchableOpacity, useTheme } from "@kolmeo/ui-core";
import { MaterialTopTabBarProps, MaterialTopTabNavigationOptions } from "@react-navigation/material-top-tabs";
import { useMediaQuery } from "@src/hooks";
import { addAlphaToHex } from "@src/lib/Kiitos";
import { useGlobalTenant } from "@src/navigation/hooks/useGlobalTenant";
import { useEffect, useRef } from "react";
import { ScrollView } from "react-native";

const TabBarItem = ({
  isFocused,
  options,
  onPress,
  onLongPress,
  routeName,
  indicatorColour,
  dataUsageId
}: {
  isFocused: boolean;
  options: MaterialTopTabNavigationOptions;
  onPress: () => void;
  onLongPress: () => void;
  routeName: string;
  indicatorColour?: string;
  dataUsageId?: string;
}) => {
  const createLabel = (options: MaterialTopTabNavigationOptions): string => {
    const { tabBarLabel, title } = options;
    if (tabBarLabel) {
      return tabBarLabel as string;
    }
    if (title) {
      return title;
    }
    return routeName;
  };

  const { colors, palette } = useTheme();

  const color = indicatorColour ?? colors.primary;

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityState={isFocused ? { selected: true } : {}}
      accessibilityLabel={options.tabBarAccessibilityLabel}
      testID={options.tabBarTestID}
      onPress={onPress}
      onLongPress={onLongPress}
      data-usage-id={dataUsageId}
    >
      <Box paddingY={"16px"} paddingX={"8px"} flexDirection="row" alignItems="center">
        {options.tabBarIcon && (
          <>
            {options.tabBarIcon({ focused: isFocused, color: isFocused ? color : addAlphaToHex(color) })}
            <Box width="8px" height="100%" />
          </>
        )}

        <Text color={isFocused ? palette.text.primary : palette.background.disable} variant="textHeavy14">
          {createLabel(options)}
        </Text>
      </Box>
      {isFocused && <Box backgroundColor={color} height="3px" borderRadius="1.5px" width="100%" />}
    </TouchableOpacity>
  );
};
const CustomTabBar = ({ state, descriptors, navigation, indicatorColour }: MaterialTopTabBarProps & { indicatorColour?: string }) => {
  const { isMobile } = useMediaQuery();
  const ref = useRef<ScrollView>(null);
  const { tenancyId } = useGlobalTenant();

  useEffect(() => {
    if (ref && state?.index === 3) {
      ref?.current?.scrollTo({ x: 100, y: 0, animated: true });
    }
  }, [state, isMobile]);

  return (
    <Box width="100%" backgroundColor="white">
      <Box maxWidth="1280px" width="100%" margin="0 auto" flexDirection="row" paddingX={isMobile ? "16px" : undefined}>
        <ScrollView ref={ref} horizontal={true} showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: "100%" }}>
          <Box
            alignItems="flex-start"
            flexDirection="row"
            width={isMobile ? "100%" : undefined}
            justifyContent={isMobile ? "space-between" : undefined}
          >
            {state.routes.map((route, index) => {
              const { options } = descriptors[route.key];

              const isFocused = state.index === index;

              const onPress = () => {
                const event = navigation.emit({
                  type: "tabPress",
                  target: route.key,
                  canPreventDefault: true
                });
                if (!isFocused && !event.defaultPrevented) {
                  navigation.navigate({ name: route.name, merge: true, params: { tenancyId } });
                }
                if (index === 3) {
                  ref?.current?.scrollTo({ x: 100, y: 0, animated: true });
                } else if (index === 0) {
                  ref?.current?.scrollTo({ x: 0, y: 0, animated: true });
                }
              };

              const onLongPress = () => {
                navigation.emit({
                  type: "tabLongPress",
                  target: route.key
                });
              };

              return (
                <TabBarItem
                  key={`${route.name}_${index}`}
                  isFocused={isFocused}
                  onPress={onPress}
                  onLongPress={onLongPress}
                  options={options}
                  routeName={route.name}
                  indicatorColour={indicatorColour}
                  dataUsageId={route.name === "Payments" ? "payments-tab-link-click" : undefined}
                />
              );
            })}
          </Box>
        </ScrollView>
      </Box>
    </Box>
  );
};

export default CustomTabBar;
